'use client';

import { useState } from 'react';
import { Button, TextArea, cn } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { useAiVideoGeneration } from './hooks/useAiVideoGeneration';
import { AnimatedDots } from '@/shared/ui/AnimatedDots';
import ImageWithFallback from '@/shared/ui/images/ImageWithFallback';
import {
  GtsMovementStyle,
  GtsMovementStyleValue,
  GtsVideoModel,
  GtsVideoModelValue,
} from '@/features/gts/model/gts-types';

const MOVEMENT_STYLES: { value: GtsMovementStyleValue; labelKey: string }[] = [
  {
    value: GtsMovementStyle.NATURAL,
    labelKey: 'Editor.ai-video.movement-natural',
  },
  {
    value: GtsMovementStyle.ENERGETIC,
    labelKey: 'Editor.ai-video.movement-energetic',
  },
  {
    value: GtsMovementStyle.ELEGANT,
    labelKey: 'Editor.ai-video.movement-elegant',
  },
  {
    value: GtsMovementStyle.PLAYFUL,
    labelKey: 'Editor.ai-video.movement-playful',
  },
  {
    value: GtsMovementStyle.DRAMATIC,
    labelKey: 'Editor.ai-video.movement-dramatic',
  },
];

interface AiVideoGenerationViewProps {
  imageUrl: string;
  previewImageUrl?: string;
  projectId: number;
  onCancel: () => void;
  onGenerateSuccess: () => void;
  uploadPendingFile?: () => Promise<string | null>;
}

export default function AiVideoGenerationView({
  imageUrl,
  previewImageUrl,
  projectId,
  onCancel,
  onGenerateSuccess,
  uploadPendingFile,
}: AiVideoGenerationViewProps) {
  const t = useTranslations();
  // 모델 선택 활성화 시 hidden 제거
  const [videoModel, setVideoModel] = useState<GtsVideoModelValue>(
    GtsVideoModel.KLING_TURBO,
  );

  const {
    movementStyle,
    setMovementStyle,
    customPrompt,
    setCustomPrompt,
    isGenerating,
    handleGenerate,
  } = useAiVideoGeneration({
    projectId,
    imageUrl,
    videoModel,
    onSuccess: onGenerateSuccess,
    uploadPendingFile,
  });

  return (
    <div className="flex h-full flex-col">
      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div className="flex min-h-0 flex-1 flex-col gap-md overflow-y-auto">
        {/* 소스 이미지 프리뷰 */}
        <div className="flex flex-col gap-2xs">
          <div className="relative aspect-video w-full overflow-hidden border border-sys-style-neutral-light">
            <ImageWithFallback
              src={previewImageUrl || imageUrl}
              alt="source image"
              fill
              className="object-contain"
            />
          </div>
        </div>

        {/* 무브먼트 스타일 선택 */}
        <div className="flex flex-col gap-2xs">
          <span className="text-comp-accent-small text-sys-cont-neutral-default">
            {t('Editor.ai-video.movement-style')}
          </span>
          <div className="grid grid-cols-5 gap-2xs">
            {MOVEMENT_STYLES.map((style) => {
              const isSelected = movementStyle === style.value;
              const [emoji, ...labelParts] = t(style.labelKey).split('\n');
              const label = labelParts.join('\n');
              return (
                <button
                  key={style.value}
                  type="button"
                  onClick={() => setMovementStyle(style.value)}
                  disabled={isGenerating}
                  className={cn(
                    'flex cursor-pointer flex-col items-center justify-center gap-3xs rounded-sm border-2 px-2xs py-xs transition-all',
                    isSelected
                      ? 'bg-sys-bg-primary-subtle border-sys-style-primary-default'
                      : 'hover:border-sys-style-primary-light border-sys-style-neutral-light hover:bg-sys-style-neutral-lightest',
                    isGenerating && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <span className="text-xl">{emoji}</span>
                  <span className="text-center text-comp-accent-xsmall">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 추가 지시사항 */}
        <div className="flex flex-col gap-2xs">
          <span className="text-comp-accent-small text-sys-cont-neutral-default">
            {t('Editor.ai-video.additional-instructions')}
          </span>
          <TextArea
            className="[&_textarea]:placeholder:text-sm"
            placeholder={t(
              'Editor.ai-video.additional-instructions-placeholder',
            )}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            disabled={isGenerating}
          />
        </div>
      </div>

      {/* 버튼 영역 - 하단 고정 */}
      <div className="flex shrink-0 items-center justify-end gap-2xs pt-md">
        <Button
          hierarchy="tertiary"
          size="small"
          onClick={onCancel}
          disabled={isGenerating}
        >
          {t('Common.cancel')}
        </Button>
        <Button
          hierarchy="primary"
          size="small"
          onClick={handleGenerate}
          disabled={isGenerating || (!imageUrl && !uploadPendingFile)}
        >
          {isGenerating ? (
            <span>
              {t('Editor.ai-video.generating')}
              <AnimatedDots />
            </span>
          ) : (
            t('Editor.ai-video.generate-button')
          )}
        </Button>
      </div>
    </div>
  );
}
